import { v4 as uuidv4 } from "uuid";

import {
  getCachedResult,
  hashFile,
  setCachedResult,
} from "@features/services/shipping/fileHashCache";
import type {
  CargoItem,
  ExtractCargoErrorResponse,
  ExtractCargoResponse,
} from "@features/services/shipping/types";

export async function extractCargoItems(
  files: File[],
  signal?: AbortSignal,
): Promise<CargoItem[]> {
  const allItems: CargoItem[] = [];
  const uncachedFiles: { file: File; hash: string }[] = [];

  for (const file of files) {
    const hash = await hashFile(file);
    const cached = getCachedResult(hash);
    if (cached != null) {
      allItems.push(...cached);
    } else {
      uncachedFiles.push({ file, hash });
    }
  }

  if (uncachedFiles.length === 0) {
    return allItems;
  }

  const formData = new FormData();
  for (const { file } of uncachedFiles) {
    formData.append("files", file);
  }

  const response = await fetch("/api/extract-cargo", {
    method: "POST",
    body: formData,
    signal,
  });

  if (!response.ok) {
    const errorBody = (await response.json()) as ExtractCargoErrorResponse;
    throw new Error(
      errorBody.error ?? `Extraction failed (${response.status})`,
    );
  }

  const data = (await response.json()) as ExtractCargoResponse;
  const newItems = data.items.map((item) => ({
    ...item,
    id: uuidv4(),
  }));

  // Cache per file when only one uncached file was sent.
  // When multiple files are sent together, the combined result is cached
  // under each file's hash (acceptable for MVP).
  if (uncachedFiles.length === 1) {
    setCachedResult(uncachedFiles[0].hash, newItems);
  } else {
    for (const { hash } of uncachedFiles) {
      setCachedResult(hash, newItems);
    }
  }

  allItems.push(...newItems);
  return allItems;
}
